// src/encounter/services/use-patient-reported.ts
// The patient's submitted pre-visit questionnaires for the summary's "Patient-reported" section, from QuestionBank
// `GET /api/questionnaire-responses/by-patient/{patientId}` (QuestionBank #183), merged newest first, patient-entered sets
// of the configured questionnaires only (lib/patient-reported.ts). Read-only.
//   · A GUID id is filtered by the server (`?questionnaire={id}`, one read each). A non-GUID id — the vendored PHQ-9
//     `44249-1-x` the pre-visit step sends — cannot be (the parameter is GUID-typed), so ONE unfiltered read covers all
//     of them and the app matches each response's own `questionnaire` reference.
//   · Every read asks the server for patient-entered sets only (`?source=Patient/{patientId}`, QuestionBank #183), so
//     newer staff-entered sets cannot push the patient's answers past `take`. The app still checks `source` too.
//   · No configured questionnaire, no patient, or the switch off → no request, and nothing to show.
//   · A 404 (a server without the route yet) reads as none; any other failure is an error, never "none".
import { useCallback } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { isGuid, toPatientReportedList, type PatientReportedResponse } from '../lib/patient-reported';

/** The most sets read per filtered questionnaire (the server allows 1..100). */
export const PATIENT_REPORTED_TAKE = 20;
/** The unfiltered read covers every non-GUID questionnaire at once, so it takes the server's maximum. */
export const PATIENT_REPORTED_UNFILTERED_TAKE = 100;

/** `null` = the one unfiltered read. */
export const patientReportedKey = (patientId: string | null | undefined, questionnaireId: string | null) =>
  ['patient-reported', patientId, questionnaireId] as const;

export async function fetchPatientReported(
  patientId: string,
  questionnaireId: string | null,
  signal?: AbortSignal,
): Promise<QuestionnaireResponseResource[]> {
  const source = `source=${encodeURIComponent(`Patient/${patientId}`)}`;
  const query =
    questionnaireId === null
      ? `${source}&take=${PATIENT_REPORTED_UNFILTERED_TAKE}`
      : `questionnaire=${encodeURIComponent(questionnaireId)}&${source}&take=${PATIENT_REPORTED_TAKE}`;
  try {
    const res = await get<QuestionnaireResponseResource[]>(
      `/api/questionnaire-responses/by-patient/${encodeURIComponent(patientId)}?${query}`,
      { signal },
    );
    return Array.isArray(res) ? res : [];
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return [];
    throw e;
  }
}

/** The reads to run: one per GUID id, plus one unfiltered read when any id is not a GUID. */
export function patientReportedReads(questionnaireIds: readonly string[]): (string | null)[] {
  const guids = questionnaireIds.filter(isGuid);
  return guids.length < questionnaireIds.length ? [...guids, null] : guids;
}

export type PatientReportedState =
  | { kind: 'off' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; responses: PatientReportedResponse[] };

const OFF: PatientReportedState = { kind: 'off' };

export function usePatientReported(
  patientId: string | null | undefined,
  questionnaireIds: readonly string[],
  enabled: boolean,
): PatientReportedState {
  const on = enabled && !!patientId && questionnaireIds.length > 0;
  // `combine` keeps the result referentially stable while the reads are unchanged, so the summary sections built from
  // it are not rebuilt on every render.
  const combine = useCallback(
    (results: UseQueryResult<QuestionnaireResponseResource[]>[]): PatientReportedState => {
      if (results.length === 0) return OFF;
      if (results.some((r) => r.isError)) return { kind: 'error' };
      if (results.some((r) => r.isPending)) return { kind: 'loading' };
      return {
        kind: 'ready',
        responses: toPatientReportedList(results.map((r) => r.data ?? []), patientId as string, questionnaireIds),
      };
    },
    [patientId, questionnaireIds],
  );
  const state = useQueries({
    queries: (on ? patientReportedReads(questionnaireIds) : []).map((id) => ({
      queryKey: patientReportedKey(patientId, id),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchPatientReported(patientId as string, id, signal),
    })),
    combine,
  });
  return on ? state : OFF;
}

export { OFF as PATIENT_REPORTED_OFF };
